
import React, { useState, useEffect, useRef } from 'react';
import '../styles/formulaBar.css';
import type { CellFormula } from '../types/gridTypes';
import type { FormulaFunctionMeta } from '../services/formulaEngine';
import { getMatchingFunctions, getFunctionMeta } from '../services/formulaEngine';
import { IconFx } from './icons';
import { SheetPickerDropdown, type SheetMetadata } from './SheetPickerDropdown';

interface FormulaBarProps {
  activeCellAddress: string | null; // e.g. "Row 1, Col Status"
  currentValue: any;
  currentFormula: CellFormula | undefined;
  onCommit: (valueOrFormula: string) => void;
  /** Story 6.5: AC-6.5.5 - Callback when Escape is pressed to cancel formula editing */
  onCancel?: () => void;
  onRangeCaptureRequest?: (callback: (rangeExpr: string) => void) => void;
  onRangeCaptureCancel?: () => void;
  /** Story 6.5: AC-6.5.3 - Columns for autocomplete suggestions */
  columns?: { id: string; label: string }[];
  /** Story 6.6: AC-6.6.1 - Available sheets with module info for cross-sheet references */
  availableSheets?: SheetMetadata[];
  /** Story 6.5: AC-6.5.6 - Current sheet name */
  currentSheetName?: string;
  /** Story 6.5: AC-6.5.2 - External controlled value for cell sync */
  externalValue?: string;
  /** Story 6.5: AC-6.5.2 - Callback when formula bar text changes */
  onChange?: (value: string) => void;
  /** Story 6.6: AC-6.6.6 - Callback to get columns from a referenced sheet */
  onGetSheetColumns?: (sheetName: string) => { id: string; label: string }[] | undefined;
  /** Flag to indicate if range capture is currently active */
  isCapturing?: boolean;
}

export const FormulaBar: React.FC<FormulaBarProps> = ({
  activeCellAddress,
  currentValue,
  currentFormula,
  onCommit,
  onCancel,
  onRangeCaptureRequest,
  onRangeCaptureCancel,
  columns = [],
  availableSheets = [],
  currentSheetName,
  externalValue,
  onChange,
  onGetSheetColumns,
  isCapturing = false,
}) => {
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const justCommittedRef = useRef(false);
  
  // Autocomplete State - Story 6.5: AC-6.5.3, AC-6.5.4
  // Unified suggestion type for both functions and columns
  type SuggestionItem = {
    type: 'function' | 'column';
    name: string;
    description: string;
    category?: string;
    meta?: FormulaFunctionMeta;
  };
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionQuery, setSuggestionQuery] = useState('');
  // Track suggestion type for styling/behavior differences
  const [, setSuggestionType] = useState<'function' | 'column'>('function');

  // Story 6.5: AC-6.5.6 - Sheet picker state
  const [showSheetPicker, setShowSheetPicker] = useState(false);

  // Signature Help State
  const [help, setHelp] = useState<{ meta: FormulaFunctionMeta, argIndex: number } | null>(null);

  useEffect(() => {
    if (currentFormula) {
      setText(currentFormula.raw);
    } else if (currentValue !== undefined && currentValue !== null) {
      setText(String(currentValue));
    } else {
      setText('');
    }
    // Reset UI states on cell change
    setShowSuggestions(false);
    setHelp(null);
    onRangeCaptureCancel?.();
  }, [currentValue, currentFormula, activeCellAddress]);

  // Story 6.5: AC-6.5.2 - Sync external value from cell editor
  useEffect(() => {
    if (externalValue !== undefined && externalValue !== text) {
      setText(externalValue);
      // Trigger context check for autocomplete
      const cursor = externalValue.length;
      checkContext(externalValue, cursor);
    }
  }, [externalValue]);

  // Handle inserting range from external capture
  const handleInsertRange = (expr: string) => {
      if (!inputRef.current) return;
      
      const cursor = inputRef.current.selectionStart || 0;
      const textBefore = text.substring(0, cursor);
      const textAfter = text.substring(cursor);
      
      const newText = textBefore + expr + textAfter;
      setText(newText);
      
      // Restore focus and move caret
      setTimeout(() => {
          if (inputRef.current) {
              inputRef.current.focus();
              const newCursor = cursor + expr.length;
              inputRef.current.setSelectionRange(newCursor, newCursor);
          }
      }, 0);
  };

  // --- Context Parsing Logic ---

  const checkContext = (inputText: string, cursor: number) => {
    if (!inputText.startsWith('=')) {
        setShowSuggestions(false);
        setHelp(null);
        onRangeCaptureCancel?.();
        return;
    }

    // 1. Signature Help (Are we inside a function?)
    // Simple parser: Look backwards for the nearest unclosed function call
    let depth = 0;
    let argIdx = 0;
    let funcName: string | null = null;
    let isInArgs = false;
    
    for (let i = cursor - 1; i >= 0; i--) {
        const char = inputText[i];
        if (char === ')') {
            depth++;
        } else if (char === '(') {
            if (depth > 0) {
                depth--;
            } else {
                // Found the opening paren of current scope
                // Capture function name preceding it
                const prefix = inputText.substring(0, i).trimEnd();
                const match = prefix.match(/([a-zA-Z0-9_]+)$/);
                if (match) {
                    funcName = match[1];
                    isInArgs = true;
                }
                break;
            }
        } else if (char === ',' && depth === 0) {
            argIdx++;
        }
    }

    if (funcName && isInArgs) {
        const meta = getFunctionMeta(funcName);
        if (meta) {
            setHelp({ meta, argIndex: argIdx });
            // Enable range capture when inside function arguments
            onRangeCaptureRequest?.(handleInsertRange);
        } else {
            setHelp(null);
            onRangeCaptureCancel?.();
        }
    } else {
        setHelp(null);
        onRangeCaptureCancel?.();
    }

    // 2. Autocomplete - Story 6.5: AC-6.5.3 (columns) and AC-6.5.4 (functions)
    const prefixBefore = inputText.substring(0, cursor);

    // Story 6.6: AC-6.6.6 - Check for cross-sheet reference pattern [SheetName]![
    // Pattern: [SheetName]![ where user is typing column name from another sheet
    const crossSheetMatch = prefixBefore.match(/\[([^\]]+)\]!\[([^\]]*)$/);
    if (crossSheetMatch && onGetSheetColumns) {
      const [, sheetName, partialColumn] = crossSheetMatch;
      const queryUpper = partialColumn.toUpperCase();

      // Get columns from the referenced sheet
      const refSheetColumns = onGetSheetColumns(sheetName);
      if (refSheetColumns && refSheetColumns.length > 0) {
        const crossSheetSuggestions: SuggestionItem[] = refSheetColumns
          .filter(col => col.label.toUpperCase().includes(queryUpper))
          .map(col => ({
            type: 'column' as const,
            name: col.label,
            description: `Column from ${sheetName}`,
            category: sheetName,
          }));

        if (crossSheetSuggestions.length > 0) {
          setSuggestions(crossSheetSuggestions);
          setSuggestionQuery(partialColumn);
          setSuggestionType('column');
          setShowSuggestions(true);
          setSuggestionIndex(0);
          return;
        }
      }
    }

    // 2a. Check for column bracket notation [ColumnName] (local sheet)
    // Look backwards for `[` to detect column reference being typed
    let bracketStart = -1;
    for (let i = cursor - 1; i >= 0; i--) {
      if (prefixBefore[i] === '[') {
        bracketStart = i;
        break;
      }
      // Stop if we hit a closing bracket or other delimiters
      if (prefixBefore[i] === ']' || prefixBefore[i] === '=' || prefixBefore[i] === '(' || prefixBefore[i] === ',') {
        break;
      }
    }

    // Skip local column autocomplete if we're in a cross-sheet reference (already handled above)
    const isAfterSheetRef = prefixBefore.match(/\[[^\]]+\]!$/);
    if (bracketStart >= 0 && !isAfterSheetRef) {
      // We're inside a column reference [...]
      const columnQuery = prefixBefore.slice(bracketStart + 1);
      const queryUpper = columnQuery.toUpperCase();
      const columnSuggestions: SuggestionItem[] = columns
        .filter(col => col.label.toUpperCase().includes(queryUpper))
        .map(col => ({
          type: 'column' as const,
          name: col.label,
          description: 'Column reference',
        }));

      if (columnSuggestions.length > 0) {
        setSuggestions(columnSuggestions);
        setSuggestionQuery(columnQuery);
        setSuggestionType('column');
        setShowSuggestions(true);
        setSuggestionIndex(0);
        return;
      }
    }

    // 2b. Check for function name autocomplete
    const matchWord = prefixBefore.match(/([a-zA-Z0-9_]+)$/);

    if (matchWord) {
        const word = matchWord[1];
        const matches = getMatchingFunctions(word);
        if (matches.length > 0) {
            const functionSuggestions: SuggestionItem[] = matches.map(fn => ({
              type: 'function' as const,
              name: fn.name,
              description: fn.description,
              category: fn.category,
              meta: fn,
            }));
            setSuggestions(functionSuggestions);
            setSuggestionQuery(word);
            setSuggestionType('function');
            setShowSuggestions(true);
            setSuggestionIndex(0);
            return;
        }
    }

    setShowSuggestions(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setText(val);
    onChange?.(val); // Story 6.5: AC-6.5.2 - Sync to cell editor

    const cursor = e.target.selectionStart || 0;
    checkContext(val, cursor);
  };

  // Story 6.5: AC-6.5.3, AC-6.5.4 - Handle selection for both columns and functions
  const handleSelectSuggestion = (item: SuggestionItem) => {
    if (!inputRef.current) return;
    const cursor = inputRef.current.selectionStart || 0;
    const prefixBefore = text.substring(0, cursor);
    const suffixAfter = text.substring(cursor);

    let newPrefix: string;
    let newText: string;

    if (item.type === 'column') {
      // Column reference: replace from [ to cursor with [ColumnName]
      // Find the bracket start position
      let bracketStart = -1;
      for (let i = cursor - 1; i >= 0; i--) {
        if (prefixBefore[i] === '[') {
          bracketStart = i;
          break;
        }
      }
      // Replace from bracket to cursor with completed column reference
      newPrefix = prefixBefore.slice(0, bracketStart) + `[${item.name}]`;
      newText = newPrefix + suffixAfter;

      setText(newText);
      setShowSuggestions(false);

      // Move caret after the closing bracket
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.setSelectionRange(newPrefix.length, newPrefix.length);
        }
      }, 0);
    } else {
      // Function: replace the query with function name + (
      newPrefix = prefixBefore.slice(0, -suggestionQuery.length) + item.name + '(';
      newText = newPrefix + suffixAfter;

      setText(newText);
      setShowSuggestions(false);

      // Show signature help if we have the meta
      if (item.meta) {
        setHelp({ meta: item.meta, argIndex: 0 });
      }

      // Move caret and trigger range capture
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.setSelectionRange(newPrefix.length, newPrefix.length);
          // Trigger range request manually since we just entered a function
          onRangeCaptureRequest?.(handleInsertRange);
        }
      }, 0);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showSuggestions) {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSuggestionIndex(prev => (prev + 1) % suggestions.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSuggestionIndex(prev => (prev - 1 + suggestions.length) % suggestions.length);
        } else if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            handleSelectSuggestion(suggestions[suggestionIndex]);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setShowSuggestions(false);
        }
        return;
    }

    if (e.key === 'Enter') {
      onCommit(text);
      justCommittedRef.current = true;
      inputRef.current?.blur();
      setShowSuggestions(false);
      setHelp(null);
      onRangeCaptureCancel?.();
    }

    // Story 6.5: AC-6.5.5 - Escape cancels formula editing
    if (e.key === 'Escape') {
      e.preventDefault();
      setShowSuggestions(false);
      setHelp(null);
      onRangeCaptureCancel?.();
      onCancel?.();
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent) => {
      // Re-check context on navigation keys to update help/tooltip
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') {
          const cursor = inputRef.current?.selectionStart || 0;
          checkContext(text, cursor);
      }
  };

  const handleClick = () => {
      const cursor = inputRef.current?.selectionStart || 0;
      checkContext(text, cursor);
  };

  const handleBlur = () => {
    // Commit the current text when focus is lost (e.g., clicking elsewhere)
    // But don't commit if we just committed via Enter OR if we are capturing a range
    if (activeCellAddress && text !== externalValue && !justCommittedRef.current && !isCapturing) {
      onCommit(text);
    }
    // Reset the flag
    justCommittedRef.current = false;
  };

  // Story 6.5: AC-6.5.6 - Handle sheet selection for cross-sheet references
  const handleSheetSelect = (sheetName: string) => {
    if (!inputRef.current) return;

    const cursor = inputRef.current.selectionStart || 0;
    const textBefore = text.substring(0, cursor);
    const textAfter = text.substring(cursor);

    // Insert [SheetName]! at cursor position
    const sheetRef = `[${sheetName}]!`;
    const newText = textBefore + sheetRef + textAfter;

    setText(newText);
    setShowSheetPicker(false);

    // Move caret after the sheet reference
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        const newCursor = cursor + sheetRef.length;
        inputRef.current.setSelectionRange(newCursor, newCursor);
      }
    }, 0);
  };

  return (
    <div className="formula-bar-container relative formula-bar-enter">
      {/* Story 6.5: AC-6.5.2 - Always show fx icon, cell address beside it */}
      <div className="formula-fx-icon">
        <IconFx className="w-4 h-4" />
      </div>
      <div className="formula-cell-ref">
        {activeCellAddress || <span className="formula-no-selection">No cell selected</span>}
      </div>
      <input
        ref={inputRef}
        className="formula-input"
        value={text}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onBlur={handleBlur}
        onClick={handleClick}
        placeholder={activeCellAddress ? "Enter value or =Formula" : "Select a cell"}
        disabled={!activeCellAddress}
        autoComplete="off"
        spellCheck={false}
      />

      {/* Story 6.6: AC-6.6.1 - Sheet Picker with module grouping for cross-sheet references */}
      {availableSheets.length > 1 && (
        <div className="formula-sheet-picker-container">
          <button
            type="button"
            className="formula-sheet-picker-btn"
            onClick={() => setShowSheetPicker(!showSheetPicker)}
            title="Insert sheet reference"
            disabled={!activeCellAddress}
          >
            <span className="formula-sheet-icon">📋</span>
            <span className="formula-sheet-label">{currentSheetName || 'Sheet'}</span>
          </button>

          {showSheetPicker && (
            <SheetPickerDropdown
              sheets={availableSheets}
              currentSheetName={currentSheetName}
              onSelect={handleSheetSelect}
              onClose={() => setShowSheetPicker(false)}
            />
          )}
        </div>
      )}

      {/* Signature Help Tooltip */}
      {help && (
          <div className="formula-help-tooltip">
              <div className="formula-help-header">
                  <span className="formula-fn-name font-bold">{help.meta.name}</span>
                  <span className="formula-paren">(</span>
                  {help.meta.signature.replace(/^[^(]*\(/, '').replace(/\)$/, '').split(',').map((arg, idx, arr) => (
                      <React.Fragment key={idx}>
                          <span className={idx === help.argIndex ? 'formula-arg-active font-bold underline underline-offset-4' : 'formula-arg'}>
                              {arg.trim()}
                          </span>
                          {idx < arr.length - 1 && <span className="formula-separator mr-1">, </span>}
                      </React.Fragment>
                  ))}
                  <span className="formula-paren">)</span>
              </div>
              <div className="formula-help-desc">{help.meta.description}</div>
          </div>
      )}

      {/* Autocomplete Menu - Story 6.5: AC-6.5.3, AC-6.5.4 */}
      {showSuggestions && suggestions.length > 0 && (
          <div className="formula-autocomplete-menu">
              {suggestions.map((item, idx) => (
                  <div
                    key={`${item.type}-${item.name}`}
                    className={`formula-autocomplete-item ${idx === suggestionIndex ? 'active' : ''}`}
                    onClick={() => handleSelectSuggestion(item)}
                  >
                      <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                              <span className={`formula-item-badge text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                item.type === 'column'
                                  ? 'formula-badge-column'
                                  : 'formula-badge-function'
                              }`}>
                                {item.type === 'column' ? 'Col' : 'Fn'}
                              </span>
                              <span className="formula-item-name font-bold text-sm">{item.name}</span>
                          </div>
                          {item.category && (
                              <span className="formula-item-category text-[10px] uppercase tracking-wider">{item.category}</span>
                          )}
                      </div>
                      <div className="formula-item-desc text-xs truncate">{item.description}</div>
                  </div>
              ))}
          </div>
      )}
    </div>
  );
};
